<template>
  <div class="v-bar-chart-table">
    <table>
      <HeadlessList v-model:hover="props.hover" :items="props.items" :select-mode="SelectMode.None"
        :allow-unselect="true">
        <template #default="{ item, onItemClick, isHover }">
          <tbody :ref="(el) => setItemRef(el as Element | null, item)" tabindex="0" :class="{
            'v-bar-chart-table__item--hover': isHover,
            'x-highlight': props.highlight && item === props.highlight
          }" @click="onClick(item); onItemClick(item)" @keydown.enter="onClick(item)" @mouseover="onMouseOver(item)"
            @mouseout="onMouseOut(item)">
            <template v-if="props.twoRowsLayout">
              <tr>
                <td class="v-bar-chart-table__col-name">
                  {{ props.labelFunc(item) }}
                </td>
                <td class="v-bar-chart-table__col-value">
                  {{
                    formatNumber(props.valueFunc(item),
                      props.numberFormatOptions) }}
                </td>
              </tr>
              <tr>
                <td colspan="2" class="v-bar-chart-table__col-bar">
                  <ProgressBar :color-func="props.barColorFunc" :value="props.valueFunc(item)" :min="minValue"
                    :max="maxValue" :show-value="false"
                    :tooltip-func="(value: number) => formatNumber(value, props.numberFormatOptions)" />
                </td>
              </tr>
            </template>

            <tr v-else>
              <td class="v-bar-chart-table__col-name">
                {{ props.labelFunc(item) }}
              </td>
              <td class="v-bar-chart-table__col-bar">
                <ProgressBar :color-func="props.barColorFunc" :value="props.valueFunc(item)" :min="minValue"
                  :max="maxValue" :show-value="false"
                  :tooltip-func="(value: number) => formatNumber(value, props.numberFormatOptions)" />
              </td>
              <td class="v-bar-chart-table__col-value">
                {{
                  formatNumber(props.valueFunc(item),
                    props.numberFormatOptions) }}
              </td>
            </tr>
          </tbody>
        </template>
      </HeadlessList>
    </table>
  </div>
</template>


<script setup lang="ts">
import { formatNumber, FormatNumberOptions, ProgressBar, HeadlessList, SelectMode } from 'gertrud';
import { computed } from 'vue';

const props = withDefaults(defineProps<{
  barColorFunc?: (value: number) => string
  items: object[]
  minOverride?: number
  maxOverride?: number
  hover?: object | null
  highlight?: object | null
  labelFunc: (item: any) => string
  numberFormatOptions?: FormatNumberOptions
  valueFunc: (item: any) => number
  twoRowsLayout?: boolean
}>(), {
  items: () => [],
  numberFormatOptions: () => ({}),
  twoRowsLayout: false
})

const emit = defineEmits<{
  'update:hover': [item: object | null]
  'update:selected': [items: object[]]
  'item-click': [payload: { item: any }]
}>();



const maxValue = computed(() => {

  if (typeof props.maxOverride === "number") return props.maxOverride;

  return Math.max(...props.items.map(i => props.valueFunc(i)));
})


const minValue = computed(() => {

  if (typeof props.minOverride === "number") return props.minOverride;

  return Math.min(...props.items.map(i => props.valueFunc(i)));
})



function onClick(item: any) {
  emit('update:selected', [item]);

  emit('item-click', { item: item });
}
function onMouseOver(item: any) {
  emit('update:hover', item);
}

function onMouseOut(item: any) {
  emit('update:hover', null);
}

// #region Scroll to item
const itemElements = new Map<any, Element>();

function setItemRef(el: Element | null, item: any) {
  if (el) itemElements.set(item, el);
  else itemElements.delete(item);
}

function scrollToItem(item: any) {
  itemElements.get(item)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
}

defineExpose({ scrollToItem });
// #endregion Scroll to item
</script>
